import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
// Removed circular dependency - credits will be awarded in checkout
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  doc, 
  updateDoc,
  setDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

const OrderContext = createContext();

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};

export const OrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentUser, refreshUserProfile } = useAuth();

  // Create a new order
  const createOrder = async (orderData) => {
    try {
      setLoading(true);
      
      // Handle guest checkout
      if (orderData.isGuest) {
        // For guest orders, no user authentication required
        if (!orderData.shippingAddress?.email) {
          throw new Error('Guest email is required for guest checkout');
        }
      } else {
        // SECURITY CHECK: Ensure user can only create orders for themselves
        if (!currentUser?.uid) {
          throw new Error('User not authenticated');
        }
        
        // Validate that the order is being created for the current user
        if (orderData.userId && orderData.userId !== currentUser.uid) {
          throw new Error('Users can only create orders for themselves');
        }
      }
      
      // Only save address if user checked the save option and is logged in
      if (orderData.saveAddress && !orderData.isGuest) {
        
        const userData = {
          uid: currentUser.uid,
          email: orderData.shippingAddress.email || currentUser.email,
          displayName: currentUser.displayName,
          firstName: orderData.shippingAddress.firstName,
          lastName: orderData.shippingAddress.lastName,
          phone: orderData.shippingAddress.phone,
          address: orderData.shippingAddress.address,
          city: orderData.shippingAddress.city,
          landmark: orderData.shippingAddress.landmark,
          state: orderData.shippingAddress.state,
          pinCode: orderData.shippingAddress.pinCode,
          createdAt: serverTimestamp()
        };

        // Add the new address
        const newAddress = {
          id: Date.now().toString(), // Simple unique ID
          ...orderData.shippingAddress,
          default: orderData.setAsDefault || false,
          savedAt: new Date() // Use regular Date instead of serverTimestamp()
        };
        
        
        const userResult = await createOrUpdateUser(userData, newAddress);
        
        if (userResult.success) {
          // Refresh the user profile to show updated data
          try {
            await refreshUserProfile();
          } catch (refreshError) {
            console.warn('Failed to refresh user profile:', refreshError);
          }
        }
      }
      
      // Only create/update user document for authenticated users
      if (!orderData.isGuest) {
        const basicUserData = {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName,
          createdAt: serverTimestamp()
        };
        
        // If saveAddress is true, include the address data
        if (orderData.saveAddress) {
          basicUserData.firstName = orderData.shippingAddress.firstName;
          basicUserData.lastName = orderData.shippingAddress.lastName;
          basicUserData.phone = orderData.shippingAddress.phone;
        }
        
        const userResult = await createOrUpdateUser(basicUserData); // eslint-disable-line no-unused-vars
      }
      
      const orderDoc = {
        // Handle both guest and authenticated user orders
        userId: orderData.isGuest ? null : currentUser.uid,
        userEmail: orderData.isGuest ? orderData.shippingAddress.email : currentUser.email,
        isGuest: orderData.isGuest || false,
        guestEmail: orderData.isGuest ? orderData.shippingAddress.email : null,
        ...orderData,
        // Use the status from orderData if provided, otherwise default to 'pending'
        status: orderData.status || 'pending',
        // Ensure paymentStatus is set properly
        paymentStatus: orderData.paymentStatus || 'pending',
        orderSource: 'website',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        orderNumber: generateOrderNumber()
      };

      const docRef = await addDoc(collection(db, 'orders'), orderDoc);
      
      // Create a new order object with proper timestamps for local state
      const newOrder = { 
        id: docRef.id, 
        ...orderDoc,
        createdAt: new Date(), // Use current date for local state
        updatedAt: new Date()  // Use current date for local state
      };
      
      setOrders(prev => [newOrder, ...prev]);
      
      // Credits will be awarded in checkout after payment success
      
      return { success: true, orderId: docRef.id, order: newOrder };
    } catch (error) {
      console.error('Error creating order:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };
  const updateOrder = async (orderId, updateData) => {
    try {
      setLoading(true);
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        ...updateData,
        updatedAt: new Date(),
      });
      return { success: true };
    } catch (error) {
      console.error('Failed to update order:', error);
      return { success: false, error };
    }finally {
      setLoading(false);
    }
  };
  

  // Fetch user's order history
  const fetchUserOrders = useCallback(async () => {
    if (!currentUser?.uid) {
      return;
    }
    
    try {
      setLoading(true);
      
      // First, check if the user has any orders
      const ordersQuery = query(
        collection(db, 'orders'),
        where('userId', '==', currentUser.uid)
      );
      
      const querySnapshot = await getDocs(ordersQuery);
      
      if (querySnapshot.empty) {
        setOrders([]);
        return { success: true, orders: [] };
      }
      
      // If there are orders, get them with ordering
      const orderedQuery = query(
        collection(db, 'orders'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const orderedSnapshot = await getDocs(orderedQuery);
      const userOrders = orderedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setOrders(userOrders);
      return { success: true, orders: userOrders };
    } catch (error) {
      console.error('Error fetching orders:', error);
      
      // If the error is about missing index, try without ordering
      if (error.message.includes('index')) {
        try {
          const simpleQuery = query(
            collection(db, 'orders'),
            where('userId', '==', currentUser.uid)
          );
          
          const simpleSnapshot = await getDocs(simpleQuery);
          const userOrders = simpleSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Sort manually in JavaScript
          userOrders.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return dateB - dateA;
          });
          
          setOrders(userOrders);
          return { success: true, orders: userOrders };
        } catch (fallbackError) {
          console.error('Fallback query also failed:', fallbackError);
          setOrders([]);
          return { success: false, error: fallbackError.message };
        }
      }
      
      setOrders([]);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Update order status (for admin use)
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setLoading(true);
      
      await updateDoc(doc(db, 'orders', orderId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setOrders(prev => 
        prev.map(order => 
          order.id === orderId 
            ? { ...order, status: newStatus, updatedAt: new Date() }
            : order
        )
      );
      
      return { success: true };
    } catch (error) {
      console.error('Error updating order status:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Get order by ID
  const getOrderById = (orderId) => {
    return orders.find(order => order.id === orderId);
  };

  // Create or update user data
  const createOrUpdateUser = async (userData, newAddress = null) => {
    try {
      // SECURITY CHECK: Ensure user can only create/update their own data
      if (!currentUser?.uid) {
        throw new Error('User not authenticated');
      }
      
      if (userData.uid !== currentUser.uid) {
        throw new Error('Users can only create/update their own data');
      }
      
      const userRef = doc(db, 'users', currentUser.uid);
      
      if (newAddress) {
        // If adding a new address, get existing user data first
        const userDoc = await getDoc(userRef);
        
        let existingAddresses = [];
        
        if (userDoc.exists()) {
          const existingUserData = userDoc.data();
          existingAddresses = existingUserData.saved_addresses || [];
          
          // If setting as default, remove default from other addresses
          if (newAddress.default) {
            existingAddresses = existingAddresses.map(addr => ({
              ...addr,
              default: false
            }));
          }
        }
        
        // Add new address
        const updatedAddresses = [...existingAddresses, newAddress];
        
        // Only save to saved_addresses array, don't update main user fields
        let userUpdateData = {
          saved_addresses: updatedAddresses,
          updatedAt: serverTimestamp()
        };
        
        await setDoc(userRef, userUpdateData, { merge: true });
      } else {
        // Just update user data without address
        const basicUserUpdateData = {
          ...userData,
          updatedAt: serverTimestamp()
        };
        
        await setDoc(userRef, basicUserUpdateData, { merge: true });
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error creating/updating user:', error);
      return { success: false, error: error.message };
    }
  };

  // Generate unique order number
  const generateOrderNumber = () => {
    const timestamp = Date.now();
    const date = new Date(timestamp);
    const year = date.getFullYear().toString().slice(-2); // Last 2 digits of year
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const random = Math.random().toString(36).substr(2, 3).toUpperCase();
    return `VV${year}${month}${day}${random}`;
  };

  // Fetch orders when user changes
  useEffect(() => {
    if (currentUser?.uid) {
      fetchUserOrders();
    } else {
      setOrders([]);
    }
  }, [currentUser?.uid, fetchUserOrders]);

  const value = {
    orders,
    loading,
    createOrder,
    updateOrder,
    fetchUserOrders,
    updateOrderStatus,
    getOrderById,
    generateOrderNumber,
    createOrUpdateUser,
    refreshUserProfile
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};

